// src/app/core/store/person/person.reducer.ts

import { createReducer, on } from '@ngrx/store';
import { AsyncSlice, CacheEntry, emptySlice, isCacheValid, initialPersonState, PersonState, TTL } from './person.state';
import { PersonByIdActions, PersonByIdNoActions, PersonSearchActions } from './person.actions';
import { PersonDetail, PersonSearchResult } from '@core/models/person.model';

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(slice: AsyncSlice<T>, data: T, ttlMs: number): AsyncSlice<T> {
  const cache: CacheEntry<T> = { data, loadedAt: Date.now(), ttlMs };
  return { data, loading: false, loaded: true, error: null, cache };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

export const personReducer = createReducer(
  initialPersonState,

  // ── Search ────────────────────────────────────────────────────────────────
  on(PersonSearchActions.search, (state): PersonState => ({
    ...state,
    search: setLoading(state.search),
  })),
  on(PersonSearchActions.searchSuccess, (state, { data }): PersonState => ({
    ...state,
    search: setSuccess(state.search, data, TTL.SEARCH),
  })),
  on(PersonSearchActions.searchFailure, (state, { error }): PersonState => ({
    ...state,
    search: setFailure(state.search, error),
  })),
  on(PersonSearchActions.clearSearch, (state): PersonState => ({
    ...state,
    search: emptySlice<PersonSearchResult[]>(),
  })),

  // ── By id ─────────────────────────────────────────────────────────────────
  on(PersonByIdActions.load, (state, { id, forceReload }): PersonState => {
    const current = state.byId[id] ?? emptySlice<PersonDetail>();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, byId: { ...state.byId, [id]: setLoading(current) } };
  }),
  on(PersonByIdActions.success, (state, { id, data }): PersonState => ({
    ...state,
    byId: { ...state.byId, [id]: setSuccess(state.byId[id] ?? emptySlice(), data, TTL.DETAIL) },
  })),
  on(PersonByIdActions.failure, (state, { id, error }): PersonState => ({
    ...state,
    byId: { ...state.byId, [id]: setFailure(state.byId[id] ?? emptySlice(), error) },
  })),
  on(PersonByIdActions.clear, (state, { id }): PersonState => {
    const { [id]: _, ...rest } = state.byId;
    return { ...state, byId: rest };
  }),

  // ── By IDNo ───────────────────────────────────────────────────────────────
  on(PersonByIdNoActions.load, (state, { idno, forceReload }): PersonState => {
    const current = state.byIdNo[idno] ?? emptySlice<PersonDetail>();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, byIdNo: { ...state.byIdNo, [idno]: setLoading(current) } };
  }),
  on(PersonByIdNoActions.success, (state, { idno, data }): PersonState => ({
    ...state,
    byIdNo: { ...state.byIdNo, [idno]: setSuccess(state.byIdNo[idno] ?? emptySlice(), data, TTL.DETAIL) },
  })),
  on(PersonByIdNoActions.failure, (state, { idno, error }): PersonState => ({
    ...state,
    byIdNo: { ...state.byIdNo, [idno]: setFailure(state.byIdNo[idno] ?? emptySlice(), error) },
  })),
  on(PersonByIdNoActions.clear, (state, { idno }): PersonState => {
    const { [idno]: _, ...rest } = state.byIdNo;
    return { ...state, byIdNo: rest };
  }),
);
